import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CssBaseline,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Divider,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import * as forge from "node-forge";

//... Icon imports
import LoadingButton from "@mui/lab/LoadingButton";
import NBA_LOGO from "../../images/nba_logo.png";
import LockIcon from "@mui/icons-material/Lock";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LoginIcon from "@mui/icons-material/Login";
import NBABackGround from "../../images/nba-login-background.jpg";
import { CircularProgress } from "@mui/material";

import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../configs/authConfig";
import { authService, UserDataService } from "../../services";
import Helper from "../../common/Helper.js";
import RouteConstants from "../../common/RouteConstants.js";

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    width: "100vw",
    // position: "relative",
    // overflow: "hidden",
  },
  applicationTitle: {
    color: "#fff",
    textTransform: "uppercase",
    fontFamily: "Action NBA Web",
    fontWeight: 500,
    letterSpacing: 1,
    fontSize: "clamp(1.5rem, 2vw, 2rem)",
  },
}));

const inputStyle = {
  borderRadius: 25,
};

export default function Login({ user }) {
  let navigate = useNavigate();
  const classes = useStyles();
  const { instance } = useMsal();

  const [username, setUserName] = useState();
  const [password, setPassword] = useState();
  const [userNameError, setUserNameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [userRedirection, setUserRedirection] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || username.trim() === "") {
      setAlert(true);
      setAlertMessage("Username is required");
      return;
    }

    if (!password || password.trim() === "") {
      setAlert(true);
      setAlertMessage("Password is required");
      return;
    }

    const publicKey = process.env.REACT_APP_PUBLIC_KEY;
    setLoadingButton(true);

    try {
      // Encrypt the password using the public key
      const rsa = forge.pki.publicKeyFromPem(publicKey);
      const encryptedPassword = window.btoa(rsa.encrypt(password));

      // Validate user credentials
      const result = await authService.validateUser(
        username,
        encryptedPassword
      );
      setLoadingButton(false);

      if (!result.data.token || result.data.token === "undefined") {
        setUserRedirection(false);
        setAlert(true);
        setAlertMessage("Invalid username or password");
        return;
      }

      // Store the token and proceed with user profile retrieval
      Helper.setStorage("accessToken", result.data.token);
      const userEmail = UserDataService.getUserEmail();

      if (!userEmail) {
        setUserRedirection(false);
        setAlert(true);
        setAlertMessage("Email is missing. Please contact admin.");
        return;
      }

      const userProfile = await UserDataService.getUserPermissionDetails(
        userEmail
      );

      if (userProfile?.appRoles?.length > 0) {
        // If appRoles exists redirect the user....
        setUserRedirection(true);
        redirectUser();
      } else if (
        userProfile?.appRoles === null ||
        userProfile?.appRoles?.length === 0
      ) {
        // If appRoles is either null or an empty array
        setUserRedirection(false);
        setAlert(true);
        setAlertMessage(
          "You might not have any permissions. Please contact admin."
        );
      }
    } catch (err) {
      setLoadingButton(false);
      console.error(err);
      setAlert(true);
      setAlertMessage("An error occurred. Please try again.");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem("checkUserAndNavigation", "true");
    setLoadingButton(true);
    instance
      .handleRedirectPromise()
      .then((tokenResponse) => {
        setLoadingButton(false);
        if (!tokenResponse) {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            // No user signed in
            instance.loginRedirect(loginRequest);
          } else {
            // Do something with the tokenResponse
            //Helper.setStorage("accessToken", tokenResponse.accessToken);
            redirectUser();
          }
        } else {
          // Do something with the tokenResponse
          //Helper.setStorage("accessToken", tokenResponse.accessToken);
          redirectUser();
        }
      })
      .catch((err) => {
        setLoadingButton(false);
        // Handle error
        console.error(err);
      });
  };

  //move this to helper component. can use in both login and app.js based on role
  function redirectUser() {
    //Redirect based on user role if needed
    let token = sessionStorage.getItem("accessToken");
    if (token !== "") {
      UserDataService.redirectToTeamDashboard().then((bRedirect) => {
        if (bRedirect) navigate(RouteConstants.TEAM_BALLOT_DASHBOARD);
        else navigate(RouteConstants.HOME);
      });
    }
  }

  const handleAlertClose = (e) => {
    setAlert(false);
  };

  return (
    <Box>
      <CssBaseline />
      <Box className={classes.rootContainer}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url(${NBABackGround}) no-repeat center center/cover`,
            filter: "blur(8px)",
            zIndex: -1,
          }}
        />

        {/* Desktop Title */}
        <Box
          sx={{
            position: "absolute",
            top: 20,
            left: 30,
            display: { xs: "none", sm: "none", md: "flex" },
            alignItems: "center",
            bgcolor: "rgba(0,0,0,0.6)",
            px: 2,
            py: 1,
            borderRadius: 2,
            boxShadow: 4,
            zIndex: 2,
          }}
        >
          <Box
            component="img"
            src={NBA_LOGO}
            alt="Logo"
            sx={{ width: 50, height: 80, mr: 1 }}
          />
          <Typography variant="h5" className={classes.applicationTitle}>
            Combine Central
          </Typography>
        </Box>

        <Paper
          elevation={6}
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            width: { xs: 310, sm: 310, md: 400 },
            maxWidth: "100%",
            textAlign: "center",
            position: "relative",
            boxSizing: "border-box",
          }}
        >
          {/* Mobile Title */}
          <Box
            sx={{
              display: { xs: "flex", sm: "flex", md: "flex", lg: "none" },
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box
              component="img"
              src={NBA_LOGO}
              alt="Logo"
              sx={{ width: 30, height: 40 }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#06255b",
                fontFamily: "Action NBA Web",
                textTransform: "uppercase",
                fontSize: "1.1rem",
              }}
            >
              Combine Central
            </Typography>
          </Box>

          {/* Lock Icon */}
          <Box
            sx={{
              position: "absolute",
              top: -35,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "#d32f2f",
              borderRadius: "50%",
              p: { xs: 1, sm: 1.5, md: 2 },
              boxShadow: 3,
            }}
          >
            <LockIcon fontSize="large" sx={{ color: "#fff" }} />
          </Box>

          {/* Desktop Text */}
          <Typography
            variant="h6"
            color="#06255b"
            mt={2}
            mb={2}
            sx={{ display: { xs: "none", sm: "none", md: "block" } }}
          >
            Log In With
          </Typography>

          {/* Caption for SSO Login */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 1,
              color: "text.secondary",
              textAlign: "center",
              lineHeight: 1.3,
            }}
          >
            For NBA & Team Users — sign in using SSO
          </Typography>

          {/* SSO Button */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<VpnKeyIcon />}
            sx={{
              mb: 2,
              borderRadius: 25,
              bgcolor: "#d32f2f",
              "&:hover": { bgcolor: "#9a0007" },
              color: "#fff",
            }}
            onClick={(e) => handleLogin(e)}
          >
            <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
              Login With SSO
            </Box>
            <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
              SSO
            </Box>
          </Button>

          <Divider sx={{ my: 2 }}>OR</Divider>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              error={userNameError}
              size="small"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your Email"
              variant="outlined"
              InputProps={{
                style: inputStyle,
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircleIcon sx={{ color: "#06255b" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              error={passwordError}
              size="small"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your Password"
              variant="outlined"
              InputProps={{
                style: inputStyle,
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon sx={{ color: "#06255b" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                mt: 1,
                color: "text.secondary",
                textAlign: "center",
                lineHeight: 1.3,
              }}
            >
              Sign in using NBA provided Login details
            </Typography>

            {loadingButton ? (
              <LoadingButton
                loading
                loadingIndicator={
                  <CircularProgress color="inherit" size={24} />
                }
                fullWidth
                variant="contained"
                sx={{
                  borderRadius: 25,
                }}
              >
                Submit
              </LoadingButton>
            ) : (
              <Button
                fullWidth
                variant="contained"
                type="submit"
                startIcon={<LoginIcon />}
                sx={{
                  borderRadius: 25,
                  bgcolor: "#06255b",
                  "&:hover": { bgcolor: "#041a40" },
                }}
              >
                LOGIN
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={alert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={handleAlertClose}
      >
        <Alert
          onClose={handleAlertClose}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
